import * as React from 'react';

import { BrokeredOrderLineItemUpdate } from '../_components/_shared/LineItems/types';
import { useTranslations } from '../i18n/useTranslations';
import {
  BrokeredOrderValidationError,
  ItemMarginRecord,
} from '../types/brokeredOrder';
import { BrokeredOrderLineItemRecord } from '../types/brokeredOrderLineItem';
import {
  computeHasUnaddedOrderItems,
  computeLineItemTotals,
  computeValidationErrorsByItemId,
  createEmptyLineItem,
  createMarginsMap,
  getMarginAutoFillForItem,
  ZERO_MONEY,
} from '../utils/lineItemCalculations';

const createLineItemFromMargin = (
  margin: ItemMarginRecord,
): BrokeredOrderLineItemRecord => {
  const base = {
    purchaseUnitPrice: margin.purchaseUnitPrice ?? ZERO_MONEY,
    salesUnitPrice: margin.salesUnitPrice ?? ZERO_MONEY,
    quantity: margin.quantity,
  };
  return {
    id: `temp-${Date.now()}-${margin.itemId}`,
    itemId: margin.itemId,
    item: null,
    quantity: margin.quantity,
    unit: margin.unit,
    purchaseUnitPrice: base.purchaseUnitPrice,
    salesUnitPrice: base.salesUnitPrice,
    ...computeLineItemTotals(base),
    notes: null,
  };
};

const applyUpdateToLineItem = (
  lineItem: BrokeredOrderLineItemRecord,
  updates: BrokeredOrderLineItemUpdate,
  marginsMap: Map<string, ItemMarginRecord>,
): BrokeredOrderLineItemRecord => {
  const merged = {
    ...lineItem,
    ...updates,
    unit: updates.unit ?? lineItem.unit,
  };

  if (updates.itemId !== undefined && updates.itemId !== lineItem.itemId) {
    const autoFill = getMarginAutoFillForItem(updates.itemId ?? '', marginsMap);
    merged.purchaseUnitPrice = autoFill.purchaseUnitPrice ?? ZERO_MONEY;
    merged.salesUnitPrice = autoFill.salesUnitPrice ?? ZERO_MONEY;
    if (autoFill.unit) merged.unit = autoFill.unit;
  }

  return { ...merged, ...computeLineItemTotals(merged) };
};

type UseBrokeredOrderLineItemsManagerOptions = {
  lineItems: BrokeredOrderLineItemRecord[];
  onLineItemsChange: (items: BrokeredOrderLineItemRecord[]) => void;
  marginsData: ItemMarginRecord[];
  serverValidationErrors?: BrokeredOrderValidationError[];
};

export const useBrokeredOrderLineItemsManager = ({
  lineItems,
  onLineItemsChange,
  marginsData,
  serverValidationErrors,
}: UseBrokeredOrderLineItemsManagerOptions) => {
  const t = useTranslations();

  const marginsMap = React.useMemo(
    () => createMarginsMap(marginsData),
    [marginsData],
  );

  const hasLinkedOrders = React.useMemo(
    () => marginsData.length > 0,
    [marginsData],
  );

  const validationErrorsByItemId = React.useMemo(
    () =>
      computeValidationErrorsByItemId({
        lineItems,
        marginsMap,
        hasLinkedOrders,
        serverValidationErrors,
        translations: {
          ITEM_NOT_IN_SALES_ORDERS:
            t.orders.validation.errors.ITEM_NOT_IN_SALES_ORDERS,
          ITEM_NOT_IN_PURCHASE_ORDERS:
            t.orders.validation.errors.ITEM_NOT_IN_PURCHASE_ORDERS,
          INSUFFICIENT_PO_QUANTITY:
            t.orders.validation.errors.INSUFFICIENT_PO_QUANTITY,
        },
      }),
    [lineItems, marginsMap, hasLinkedOrders, serverValidationErrors, t],
  );

  const hasUnaddedOrderItems = React.useMemo(
    () => computeHasUnaddedOrderItems(lineItems, marginsMap),
    [lineItems, marginsMap],
  );

  const addLineItem = React.useCallback(() => {
    onLineItemsChange([...lineItems, createEmptyLineItem()]);
  }, [lineItems, onLineItemsChange]);

  const addLineItemsFromOrders = React.useCallback(() => {
    if (marginsData.length === 0) return;

    const existingItemIds = new Set(lineItems.map((li) => li.itemId));
    const newItems = marginsData
      .filter((m) => !existingItemIds.has(m.itemId))
      .map(createLineItemFromMargin);

    if (newItems.length > 0) {
      onLineItemsChange([...lineItems, ...newItems]);
    }
  }, [lineItems, marginsData, onLineItemsChange]);

  const updateLineItem = React.useCallback(
    (id: string, updates: BrokeredOrderLineItemUpdate) => {
      const updatedItems = lineItems.map((li) => {
        if (li.id !== id) return li;
        return applyUpdateToLineItem(li, updates, marginsMap);
      });
      onLineItemsChange(updatedItems);
    },
    [lineItems, marginsMap, onLineItemsChange],
  );

  const deleteLineItem = React.useCallback(
    (id: string) => {
      onLineItemsChange(lineItems.filter((li) => li.id !== id));
    },
    [lineItems, onLineItemsChange],
  );

  return {
    addLineItem,
    addLineItemsFromOrders,
    updateLineItem,
    deleteLineItem,
    validationErrorsByItemId,
    hasUnaddedOrderItems,
    hasLinkedOrders,
  };
};
